import axios from 'axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put } from 'redux-saga/effects';
import { setSingleWorkflow, setAllWorkflows, setWorkflowExecutions, setExecutionDetails, setAllWorkflowTemplates, setWorkflowVersions, setVersionComparison, setWorkflowSchedules, setWorkflowAnalytics, setWorkspaceAnalytics, setLangGraphNodeTypes, setLangGraphNodeMetadata, setSystemMetrics, setCheckpoints, setContactTags} from './index';

import { ApiUrl, LangGraphApiUrl, GTM_TRACK_MODE } from '../config';
import { trackWorkflowCreated } from '../../utils/trackingHelper';

// API call helpers
function* getCall(path) {
  try {
    const response = yield call(() => axios.get(path));
    return { response };
  } catch (error) {
    return { error };
  }
}

function* postCall({ path, payload }) {
  try {
    const response = yield call(() => axios.post(path, payload));
    return { response };
  } catch (error) {
    return { error };
  }
}

function* putCall({ path, payload }) {
  try {
    const response = yield call(() => axios.put(path, payload));
    return { response };
  } catch (error) {
    return { error };
  }
}

function* deleteCall(path) {
  try {
    const response = yield call(() => axios.delete(path));
    return { response };
  } catch (error) {
    return { error };
  }
}

// Fetch all workflows
function* getAllWorkflows({ payload = {} }) {
  yield put({ type: 'SET_LOADING' });
  
  // Build query parameters from payload
  const params = new URLSearchParams();
  if (payload.status) params.append('status', payload.status);
  if (payload.type) params.append('type', payload.type);
  if (payload.search) params.append('search', payload.search);

  // Construct URL with query parameters
  const queryString = params.toString();
  const url = queryString ? `${LangGraphApiUrl}?${queryString}` : LangGraphApiUrl;

  const { error, response } = yield call(getCall, url);
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch workflows' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch workflows');
  } else if (response) {
    yield put(setAllWorkflows(response.data.body || response.data));
    yield put({ type: 'SET_ERROR', payload: null });
  }
}

// Fetch single workflow
function* getWorkflowById({ payload }) {
  const { error, response } = yield call(getCall, `${LangGraphApiUrl}/${payload}`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch workflow' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch workflow');
  } else if (response) {
    yield put(setSingleWorkflow(response.data.body?.workflow || response.data.workflow || response.data));
  }
}

// Create workflow
function* createWorkflow({ payload }) {
  const { data, successCallback, failCallback } = payload;
  
  const { error, response } = yield call(postCall, { path: `${LangGraphApiUrl}`, payload: data });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to create workflow' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to create workflow');
  } else if (response) {
    const workflow = response.data.body?.workflow || response.data.workflow || response.data;
    yield put({ type: 'CREATE_WORKFLOW_SUCCESS', payload: workflow });
    if (successCallback) successCallback(workflow);
    EventBus.publish('success', 'Workflow created successfully');

    // Track workflow created (GTM + Meta)
    trackWorkflowCreated(workflow);
  }
}

// Update workflow
function* updateWorkflowById({ payload }) {
  const { id, data, successCallback, failCallback } = payload;

  
  const { error, response } = yield call(putCall, { path: `${LangGraphApiUrl}/${id}`, payload: data });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to update workflow' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to update workflow');
  } else if (response) {
    const workflow = response.data.body?.workflow || response.data.workflow || response.data;

    yield put({ type: 'UPDATE_WORKFLOW_SUCCESS', payload: workflow });
    if (successCallback) successCallback(workflow);
    EventBus.publish('success', 'Workflow updated successfully');
  }
}

// Delete workflow
function* deleteWorkflowById({ payload }) {
  const { error, response } = yield call(deleteCall, `${LangGraphApiUrl}/${payload}`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to delete workflow' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to delete workflow');
  } else if (response) {
    yield put({ type: 'DELETE_WORKFLOW_SUCCESS', payload });
    EventBus.publish('success', 'Workflow deleted successfully');
    // Navigate back to workflows list
    window.location.href = '#/agent/workflows';
    // Refresh workflows list
    yield put({ type: 'GET_ALL_WORKFLOWS' });
  }
}

// Batch delete workflows
function* batchDeleteWorkflows({ payload }) {
  const workflowIds = payload;
  let successCount = 0;
  let errorCount = 0;
  
  // Delete each workflow
  for (const id of workflowIds) {
    const { error, response } = yield call(deleteCall, `${LangGraphApiUrl}/${id}`);
    if (response) {
      successCount++;
    } else if (error) {
      errorCount++;
    }
  }
  
  if (successCount > 0) {
    yield put({ type: 'BATCH_DELETE_WORKFLOWS_SUCCESS', payload: workflowIds });
    EventBus.publish('success', `Successfully deleted ${successCount} workflow${successCount > 1 ? 's' : ''}`);
    // Refresh workflows list
    yield put({ type: 'GET_ALL_WORKFLOWS' });
  }
  
  if (errorCount > 0) {
    EventBus.publish('error', `Failed to delete ${errorCount} workflow${errorCount > 1 ? 's' : ''}`);
  }
}

// Clone workflow
function* cloneWorkflowById({ payload }) {
  const { id, name, description, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, {
    path: `${ApiUrl}/automation/workflows/${id}/clone`,
    payload: { name, description }
  });

  if (error) {
    const errorMessage = error?.response?.data?.message || 'Failed to clone workflow';
    yield put({ type: 'CLONE_WORKFLOW_FAIL', payload: errorMessage });
    yield put({ type: 'SET_ERROR', payload: errorMessage });
    EventBus.publish('error', errorMessage);

    // Call fail callback if provided
    if (failCallback) {
      failCallback(error);
    }
    return;
  }

  if (response) {
    const clonedWorkflow = response.data.body.workflow;
    yield put({ type: 'CLONE_WORKFLOW_SUCCESS', payload: clonedWorkflow });
    EventBus.publish('success', 'Workflow cloned successfully');

    // Call success callback if provided
    if (successCallback) {
      successCallback(clonedWorkflow);
    }

    return response.data.body;
  }
}

// Execute workflow
function* executeWorkflow({ payload }) {
  const { id, input, options = {}, successCallback, failCallback } = payload;
  yield put({ type: 'SET_LOADING', payload: true });
  EventBus.publish('success', 'Workflow execution started');
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/${id}/execute`, 
    payload: { input, options } 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to execute workflow' });
    yield put({ type: 'SET_LOADING', payload: false });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to execute workflow');
  } else if (response) {
    yield put({ type: 'EXECUTE_WORKFLOW_SUCCESS', payload: response.data });
    if (successCallback) successCallback(response.data);
    EventBus.publish('success', 'Workflow execution successful');
    yield put({ type: 'SET_LOADING', payload: false });
  }
}

// Get workflow executions
function* getWorkflowExecutions({ payload }) {
  const { workflowId, page, limit, status, append, ...otherParams } = payload;

  // Build query string only if pagination params are provided
  const queryParams = new URLSearchParams();
  if (page) queryParams.append('page', page);
  if (limit) queryParams.append('limit', limit);
  if (status) queryParams.append('status', status);

  // Add any other params
  Object.keys(otherParams).forEach(key => {
    if (otherParams[key] !== undefined && otherParams[key] !== null) {
      queryParams.append(key, otherParams[key]);
    }
  });

  const queryString = queryParams.toString();
  const url = queryString
    ? `${LangGraphApiUrl}/${workflowId}/executions?${queryString}`
    : `${LangGraphApiUrl}/${workflowId}/executions`;

  const { error, response } = yield call(getCall, url);

  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch executions' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch executions');
  } else if (response) {
    const data = response.data.body || response.data;
    yield put(setWorkflowExecutions({
      ...data,
      append: append || false  // Pass append flag to reducer
    }));
  }
}

// Get execution details
function* getExecutionDetails({ payload }) {
  const { error, response } = yield call(getCall, `${LangGraphApiUrl}/executions/${payload}`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch execution details' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch execution details');
  } else if (response) {
    yield put(setExecutionDetails(response.data.body?.execution || response.data.execution || response.data));
  }
}

// Get workflow templates
function* getAllWorkflowTemplates() {
  const { error, response } = yield call(getCall, `${LangGraphApiUrl}/templates`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch templates' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch templates');
  } else if (response) {
    yield put(setAllWorkflowTemplates(response.data.body?.templates || response.data.templates || []));
  }
}

// Create from template
function* createFromTemplate({ payload }) {
  const { templateId, data, successCallback, failCallback } = payload;
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/templates/${templateId}/create`, 
    payload: data 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to create from template' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to create from template');
  } else if (response) {
    const workflow = response.data.body?.workflow || response.data.workflow || response.data;
    yield put({ type: 'CREATE_WORKFLOW_SUCCESS', payload: workflow });
    if (successCallback) successCallback(workflow);
    EventBus.publish('success', 'Workflow created from template successfully');
  }
}

// Get workflow versions
function* getWorkflowVersions({ payload }) {
  const { error, response } = yield call(getCall, `/automation/workflows/${payload}/versions`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch versions' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch versions');
  }
  
  if (response) {
    yield put(setWorkflowVersions(response.data.body.versions));
  }
}

// Create workflow version
function* createWorkflowVersion({ payload }) {
  const { workflowId, data, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { 
    path: `/automation/workflows/${workflowId}/versions`, 
    payload: data 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to create version' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to create version');
  }
  
  if (response) {
    yield put({ type: 'CREATE_WORKFLOW_VERSION_SUCCESS', payload: response.data.body.version });
    if (successCallback) successCallback(response.data.body.version);
    EventBus.publish('success', 'Version created successfully');
  }
}

// Restore workflow version
function* restoreWorkflowVersion({ payload }) {
  const { workflowId, versionId, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { 
    path: `/automation/workflows/${workflowId}/versions/${versionId}/restore` 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to restore version' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to restore version');
  }
  
  if (response) {
    yield put({ type: 'RESTORE_WORKFLOW_VERSION_SUCCESS', payload: response.data.body.workflow });
    if (successCallback) successCallback(response.data.body.workflow);
    EventBus.publish('success', 'Version restored successfully');
  }
}

// Compare workflow versions
function* compareWorkflowVersions({ payload }) {
  const { workflowId, versionIds } = payload;
  const { error, response } = yield call(postCall, { 
    path: `/automation/workflows/${workflowId}/versions/compare`,
    payload: { versionIds }
  });
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to compare versions' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to compare versions');
  }
  
  if (response) {
    yield put(setVersionComparison(response.data.body.comparison));
  }
}

// Get workflow schedules
function* getWorkflowSchedules({ payload }) {
  const { error, response } = yield call(getCall, `/automation/workflows/${payload}/schedules`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch schedules' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch schedules');
  }
  
  if (response) {
    yield put(setWorkflowSchedules(response.data.body.schedules));
  }
}

// Create workflow schedule
function* createWorkflowSchedule({ payload }) {
  const { data, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { 
    path: `/automation/workflows/schedules`, 
    payload: data 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to create schedule' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to create schedule');
  }
  
  if (response) {
    if (successCallback) successCallback(response.data.body.schedule);
    yield put({ type: 'SET_LOADING', payload: false });
  }
}

// Update workflow schedule
function* updateWorkflowSchedule({ payload }) {
  const { scheduleId, data, successCallback, failCallback } = payload;
  const { error, response } = yield call(putCall, { 
    path: `/automation/workflows/schedules/${scheduleId}`, 
    payload: data 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to update schedule' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to update schedule');
  }
  
  if (response) {
    if (successCallback) successCallback(response.data.body.schedule);
    yield put({ type: 'SET_LOADING', payload: false });
  }
}

// Delete workflow schedule
function* deleteWorkflowSchedule({ payload }) {
  const { scheduleId, successCallback, failCallback } = payload;
  const { error, response } = yield call(deleteCall, `/automation/workflows/schedules/${scheduleId}`);
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to delete schedule' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to delete schedule');
  }
  
  if (response) {
    if (successCallback) successCallback();
    yield put({ type: 'SET_LOADING', payload: false });
  }
}

// Pause workflow schedule
function* pauseWorkflowSchedule({ payload }) {
  const { scheduleId, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { 
    path: `/automation/workflows/schedules/${scheduleId}/pause` 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to pause schedule' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to pause schedule');
  }
  
  if (response) {
    if (successCallback) successCallback();
    yield put({ type: 'SET_LOADING', payload: false });
  }
}

// Resume workflow schedule
function* resumeWorkflowSchedule({ payload }) {
  const { scheduleId, successCallback, failCallback } = payload;
  const { error, response } = yield call(postCall, { 
    path: `/automation/workflows/schedules/${scheduleId}/resume` 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to resume schedule' });
    return EventBus.publish('error', error?.response?.data?.message || 'Failed to resume schedule');
  }
  
  if (response) {
    if (successCallback) successCallback();
    yield put({ type: 'SET_LOADING', payload: false });
  }
}

// Get workflow analytics
function* getWorkflowAnalytics({ payload }) {
  const { id, timeRange } = payload;
  
  try {
    const response = yield call(() => axios.get(`${ApiUrl}/automation/workflows/analytics/${id}`, { params: { timeRange } }));
    yield put(setWorkflowAnalytics(response.data.body?.analytics || response.data.body || {}));
  } catch (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || error?.message || 'Failed to fetch analytics' });
    EventBus.publish('error', error?.response?.data?.message || error?.message || 'Failed to fetch analytics');
  }
}

// Get workspace analytics
function* getWorkspaceAnalytics({ payload = {} }) {
  const { days = 7 } = payload;
  
  try {
    const response = yield call(() => axios.get(`${ApiUrl}/automation/workflows/analytics/workspace`, { params: { days } }));
    yield put(setWorkspaceAnalytics(response.data.body || response.data || {}));
  } catch (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || error?.message || 'Failed to fetch workspace analytics' });
    EventBus.publish('error', error?.response?.data?.message || error?.message || 'Failed to fetch workspace analytics');
  }
}

// LangGraph-specific saga functions
function* getLangGraphNodeTypes() {
  const { error, response } = yield call(getCall, `${LangGraphApiUrl}/node-types`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch node types' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch node types');
  } else if (response) {
    yield put(setLangGraphNodeTypes(response.data.nodeTypes || response.data));
  }
}

function* getLangGraphNodeMetadata() {
  const { error, response } = yield call(getCall, `${LangGraphApiUrl}/node-metadata`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch node metadata' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch node metadata');
  } else if (response) {
    yield put(setLangGraphNodeMetadata(response.data.metadata || response.data));
  }
}

function* validateLangGraphNode({ payload }) {
  const { nodeConfig, successCallback, failCallback } = payload;
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/validate-node`, 
    payload: { nodeConfig } 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to validate node' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to validate node');
  } else if (response) {
    const data = response.data;
    if (data.valid) {
      if (successCallback) successCallback(data);
      EventBus.publish('success', 'Node configuration is valid');
    } else {
      if (failCallback) failCallback(data);
      EventBus.publish('warning', `Node validation failed: ${data.error}`);
    }
  }
}

function* executeLangGraphWorkflow({ payload }) {
  const { workflowId, input = {}, options = {}, successCallback, failCallback } = payload;
  yield put({ type: 'SET_LOADING', payload: true });
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/${workflowId}/execute`, 
    payload: { input, options } 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_LOADING', payload: false });
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to execute workflow' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to execute workflow');
  } else if (response) {
    if (successCallback) successCallback(response.data);
    yield put({ type: 'SET_LOADING', payload: false });
    // Store execution reference for tracking
    yield put({ type: 'SET_CURRENT_EXECUTION', payload: response.data });
  }
}

function* testLangGraphWorkflow({ payload }) {
  const { workflowId, input = {}, options = {}, successCallback, failCallback } = payload;
  yield put({ type: 'SET_LOADING', payload: true });
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/${workflowId}/test`, 
    payload: { input, options } 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_LOADING', payload: false });
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to test workflow' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to test workflow');
  } else if (response) {
    if (successCallback) successCallback(response.data);
    yield put({ type: 'SET_LOADING', payload: false });
    EventBus.publish('success', 'Workflow test completed');
  }
}

function* validateLangGraphWorkflow({ payload }) {
  const { workflowId, successCallback, failCallback } = payload;
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/${workflowId}/validate` 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to validate workflow' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to validate workflow');
  } else if (response) {
    const data = response.data;
    if (data.valid) {
      if (successCallback) successCallback(data);
      EventBus.publish('success', 'Workflow is valid');
    } else {
      if (failCallback) failCallback(data);
      EventBus.publish('warning', `Workflow validation failed: ${data.error}`);
    }
  }
}

function* getLangGraphSystemMetrics() {
  const { error, response } = yield call(getCall, `${LangGraphApiUrl}/metrics`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch system metrics' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch system metrics');
  } else if (response) {
    yield put(setSystemMetrics(response.data));
  }
}

// Resume execution
function* resumeExecution({ payload }) {
  const { executionId, input = {}, options = {}, successCallback, failCallback } = payload;
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/executions/${executionId}/resume`, 
    payload: { input, options } 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    EventBus.publish('error', error?.response?.data?.message || 'Failed to resume execution');
  } else if (response) {
    if (successCallback) successCallback(response.data);
    EventBus.publish('success', 'Execution resumed successfully');
  }
}

// Cancel execution
function* cancelExecution({ payload }) {
  const { executionId, reason = 'User cancelled', successCallback, failCallback } = payload;
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/executions/${executionId}/cancel`, 
    payload: { reason } 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    EventBus.publish('error', error?.response?.data?.message || 'Failed to cancel execution');
  } else if (response) {
    if (successCallback) successCallback(response.data);
    EventBus.publish('success', 'Execution cancelled');
  }
}

// Get checkpoints
function* getCheckpoints({ payload }) {
  const { error, response } = yield call(getCall, `${LangGraphApiUrl}/executions/${payload}/checkpoints`);
  
  if (error) {
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch checkpoints');
  } else if (response) {
    yield put(setCheckpoints(response.data.checkpoints || response.data));
  }
}

// Import workflow
function* importWorkflow({ payload }) {
  const { workflowData, successCallback, failCallback } = payload;
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/import`, 
    payload: { workflowData } 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    EventBus.publish('error', error?.response?.data?.message || 'Failed to import workflow');
  } else if (response) {
    if (successCallback) successCallback(response.data);
    yield put({ type: 'GET_ALL_WORKFLOWS' });
  }
}

// Export workflow
function* exportWorkflow({ payload }) {
  const { workflowId, format = 'json', successCallback, failCallback } = payload;
  
  const { error, response } = yield call(getCall, `${LangGraphApiUrl}/${workflowId}/export?format=${format}`);
  
  if (error) {
    if (failCallback) failCallback(error);
    EventBus.publish('error', error?.response?.data?.message || 'Failed to export workflow');
  } else if (response) {
    if (successCallback) successCallback(response.data);
  }
}

// List system templates
function* listSystemTemplates({ payload }) {
  const { page = 1, limit = 20, category, search } = payload;
  
  yield put({ type: 'SET_LOADING', payload: true });
  
  const params = new URLSearchParams({ page, limit });
  if (category) params.append('category', category);
  if (search) params.append('search', search);
  
  const { error, response } = yield call(getCall, `${ApiUrl}/automation/workflows/system-templates?${params.toString()}`);
  
  if (error) {
    yield put({ type: 'SET_ERROR', payload: error?.response?.data?.message || 'Failed to fetch templates' });
    EventBus.publish('error', error?.response?.data?.message || 'Failed to fetch templates');
  } else if (response) {
    yield put({ type: 'SET_SYSTEM_TEMPLATES', payload: response.data.body });
    yield put({ type: 'SET_LOADING', payload: false });
  }
}

// Get required connections for a workflow
function* getRequiredConnections({ payload }) {
  const { workflowId } = payload;
  
  const { error, response } = yield call(getCall, `${ApiUrl}/automation/workflows/${workflowId}/required-connections`);
  
  if (error) {
    EventBus.publish('error', error?.response?.data?.message || 'Failed to check connections');
    yield put({ type: 'SET_REQUIRED_CONNECTIONS_ERROR', payload: error });
    return;
  }
  
  if (response) {
    yield put({ type: 'SET_REQUIRED_CONNECTIONS', payload: response.data.body });
  }
}

// Clone workflow (wrapper for template cloning)
function* cloneWorkflow({ payload }) {
  return yield cloneWorkflowById({ payload });
}

// Batch execute
function* batchExecute({ payload }) {
  const { executions, options = {}, successCallback, failCallback } = payload;
  
  const { error, response } = yield call(postCall, { 
    path: `${LangGraphApiUrl}/batch/execute`, 
    payload: { executions, options } 
  });
  
  if (error) {
    if (failCallback) failCallback(error);
    EventBus.publish('error', error?.response?.data?.message || 'Failed to execute batch');
  } else if (response) {
    if (successCallback) successCallback(response.data);
    EventBus.publish('success', 'Batch execution started');
  }
}

// Batch validate
function* batchValidate({ payload }) {
  const { workflowIds, successCallback, failCallback } = payload;

  const { error, response } = yield call(postCall, {
    path: `${LangGraphApiUrl}/batch/validate`,
    payload: { workflowIds }
  });

  if (error) {
    if (failCallback) failCallback(error);
    EventBus.publish('error', error?.response?.data?.message || 'Failed to validate batch');
  } else if (response) {
    if (successCallback) successCallback(response.data);
    EventBus.publish('success', 'Batch validation completed');
  }
}

// Fetch contact tags
function* getContactTagsSaga({ payload }) {
  const { contactId, successCallback, failCallback } = payload;

  // Add cache-busting timestamp to force fresh data
  const url = `${LangGraphApiUrl}/execute/crm/tags/${encodeURIComponent(contactId)}?_t=${Date.now()}`;

  const { error, response } = yield call(
    getCall,
    url
  );

  if (error) {
    yield put({ type: 'SET_CONTACT_TAGS_ERROR', payload: error?.response?.data?.message || 'Failed to fetch tags' });
    if (failCallback) failCallback(error);
  } else if (response) {
    const data = response.data.body || response.data.data || response.data;
    yield put(setContactTags(data));
    if (successCallback) successCallback(data);
  }
}


export function* workflowsSaga() {
  yield all([
    takeEvery('GET_ALL_WORKFLOWS', getAllWorkflows),
    takeEvery('GET_SINGLE_WORKFLOW', getWorkflowById),
    takeEvery('CREATE_WORKFLOW', createWorkflow),
    takeEvery('UPDATE_WORKFLOW', updateWorkflowById),
    takeEvery('DELETE_WORKFLOW', deleteWorkflowById),
    takeEvery('BATCH_DELETE_WORKFLOWS', batchDeleteWorkflows),
    takeEvery('CLONE_WORKFLOW', cloneWorkflowById),
    takeEvery('EXECUTE_WORKFLOW', executeWorkflow),
    takeEvery('GET_WORKFLOW_EXECUTIONS', getWorkflowExecutions),
    takeEvery('GET_EXECUTION_DETAILS', getExecutionDetails),
    takeEvery('GET_ALL_WORKFLOW_TEMPLATES', getAllWorkflowTemplates),
    takeEvery('CREATE_FROM_TEMPLATE', createFromTemplate),
    takeEvery('GET_WORKFLOW_VERSIONS', getWorkflowVersions),
    takeEvery('CREATE_WORKFLOW_VERSION', createWorkflowVersion),
    takeEvery('RESTORE_WORKFLOW_VERSION', restoreWorkflowVersion),
    takeEvery('COMPARE_WORKFLOW_VERSIONS', compareWorkflowVersions),
    takeEvery('GET_WORKFLOW_SCHEDULES', getWorkflowSchedules),
    takeEvery('CREATE_WORKFLOW_SCHEDULE', createWorkflowSchedule),
    takeEvery('UPDATE_WORKFLOW_SCHEDULE', updateWorkflowSchedule),
    takeEvery('DELETE_WORKFLOW_SCHEDULE', deleteWorkflowSchedule),
    takeEvery('PAUSE_WORKFLOW_SCHEDULE', pauseWorkflowSchedule),
    takeEvery('RESUME_WORKFLOW_SCHEDULE', resumeWorkflowSchedule),
    takeEvery('GET_WORKFLOW_ANALYTICS', getWorkflowAnalytics),
    takeEvery('GET_WORKSPACE_ANALYTICS', getWorkspaceAnalytics),
    
    // LangGraph-specific actions
    takeEvery('GET_LANGGRAPH_NODE_TYPES', getLangGraphNodeTypes),
    takeEvery('GET_LANGGRAPH_NODE_METADATA', getLangGraphNodeMetadata),
    takeEvery('VALIDATE_LANGGRAPH_NODE', validateLangGraphNode),
    takeEvery('EXECUTE_LANGGRAPH_WORKFLOW', executeLangGraphWorkflow),
    takeEvery('TEST_LANGGRAPH_WORKFLOW', testLangGraphWorkflow),
    takeEvery('VALIDATE_LANGGRAPH_WORKFLOW', validateLangGraphWorkflow),
    takeEvery('GET_LANGGRAPH_SYSTEM_METRICS', getLangGraphSystemMetrics),
    
    // Execution management actions
    takeEvery('RESUME_EXECUTION', resumeExecution),
    takeEvery('CANCEL_EXECUTION', cancelExecution),
    takeEvery('GET_CHECKPOINTS', getCheckpoints),
    
    // Import/Export actions
    takeEvery('IMPORT_WORKFLOW', importWorkflow),
    takeEvery('EXPORT_WORKFLOW', exportWorkflow),
    
    // Batch operations
    takeEvery('BATCH_EXECUTE', batchExecute),
    takeEvery('BATCH_VALIDATE', batchValidate),
    
    // Template-related actions
    takeEvery('LIST_SYSTEM_TEMPLATES', listSystemTemplates),
    takeEvery('GET_REQUIRED_CONNECTIONS', getRequiredConnections),
    takeEvery('CLONE_WORKFLOW_TEMPLATE', cloneWorkflow),

    // CRM operations
    takeEvery('GET_CONTACT_TAGS', getContactTagsSaga),
  ]);
}